import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import styles from './styles/EditClassroomModal.module.css'; 

const EditClassroomModal = ({ isOpen, onClose, classroom, setClassrooms }) => {
    const [classroomName, setClassroomName] = useState('');
    const [image, setImage] = useState(null); 
    const [imagePreview, setImagePreview] = useState(null); 
    const [currentImageUrl, setCurrentImageUrl] = useState(null); 
    const [error, setError] = useState(null);
    const [closing, setClosing] = useState(false);

    const fileInputRef = useRef(null); 

    useEffect(() => {
        if (classroom) {
            setClassroomName(classroom.name || '');
            setCurrentImageUrl(classroom.image || null); 
            setImage(null); 
            setImagePreview(null); 
            setError(null); 
            if (fileInputRef.current) {
                fileInputRef.current.value = ""; 
            }
            console.log("current image url: ", classroom.image);
        }
    }, [classroom, isOpen]); 

    const handleImageUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) { 
                setError('Image size should not exceed 5MB.');
                setImage(null);
                setImagePreview(null); 
                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }
                return;
            }
            if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
                setError('Invalid file type. Please upload an image (JPG, PNG, GIF, WEBP).');
                setImage(null);
                setImagePreview(null);
                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }
                return;
            }
            setError(null);
            setImage(file); 
            setImagePreview(URL.createObjectURL(file)); 
        }
    };

    const handleRemoveNewImage = () => {
        setImage(null); 
        setImagePreview(null); 
        if (fileInputRef.current) {
            fileInputRef.current.value = ""; 
        }
        setError(null);
    };

    const handleRemoveExistingImage = () => {
        setCurrentImageUrl(null); 
        setImage(null);          
        setImagePreview(null);
        setError(null);
    };

    const handleEdit = async () => {
        if (!classroomName.trim()) {
            setError("Classroom name cannot be empty.");
            return;
        }
        setError(null);

        const formData = new FormData();
        formData.append('name', classroomName);

        if (image) { 
            formData.append('image', image);
        } else if (currentImageUrl === null && classroom.image) {
            formData.append('image', ''); 
        }
        

        try {
            const response = await axios.patch(
                `${process.env.REACT_APP_API_URL_LOCAL}/api/classrooms/${classroom._id}`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } } 
            );

            
            const updatedImage = response.data?.classroom?.image || (imagePreview ? imagePreview : currentImageUrl);

            setClassrooms((prevClassrooms) =>
                prevClassrooms.map((item) =>
                    item._id === classroom._id
                        ? { ...item, name: classroomName, image: updatedImage }
                        : item
                )
            );
            triggerClose();
        } catch (error) {
            const errorMessage = error.response?.data?.errorMessage || error.message || 'Failed to update the classroom.';
            setError(errorMessage);
            console.error("Error updating classroom:", error);
        }
    };

    const triggerClose = () => {
        setClosing(true);
        setTimeout(() => {
            onClose(); 
            setImage(null);
            setImagePreview(null);
            setError(null);
            setClosing(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }, 300);
    };

    
    useEffect(() => {
        const tempImagePreview = imagePreview; 
        return () => {
            if (tempImagePreview && tempImagePreview.startsWith('blob:')) {
                URL.revokeObjectURL(tempImagePreview);
            }
        };
    }, [imagePreview]);


    if (!isOpen && !closing) return null;

    
    const displayImageUrl = imagePreview || currentImageUrl;

    return (
        <div className={`${styles.modalOverlay} ${closing ? styles.fadeOut : styles.fadeIn}`}>
            <div className={`${styles.modalContent} ${styles.wideModal}`}>
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>Edit Classroom</h2>
                </div>
                <div className={styles.modalBody}>
                    <input
                        type="text"
                        className={styles.modalInput}
                        value={classroomName}
                        onChange={(e) => setClassroomName(e.target.value)}
                        placeholder="Classroom Name"
                        aria-label="Classroom Name"
                    />
                    <input
                        type="file"
                        id="editClassroomImageUpload"
                        ref={fileInputRef}
                        onChange={handleImageUpload}
                        style={{ display: 'none' }}
                        accept="image/png, image/jpeg, image/gif, image/webp"
                    />
                    <label htmlFor="editClassroomImageUpload" className={styles.uploadImageButton}>
                        {imagePreview ? 'Change Selected Image' : (currentImageUrl ? 'Change Classroom Image' : 'Add Classroom Image')}
                    </label>
                    {displayImageUrl && (
                        <div className={styles.imagePreviewContainer}>
                            <img src={displayImageUrl} alt="Classroom Preview" className={styles.imagePreview} />
                            {imagePreview ? ( 
                                <button
                                    onClick={handleRemoveNewImage}
                                    className={styles.removeImageButton}
                                    aria-label="Cancel image change"
                                >
                                    Cancel Change
                                </button>
                            ) : ( 
                                currentImageUrl && (
                                <button
                                    onClick={handleRemoveExistingImage}
                                    className={styles.removeImageButton}
                                    aria-label="Remove classroom image"
                                >
                                    Remove Image
                                </button>
                                )
                            )}
                        </div>
                    )}
                    {error && <div className={styles.error}>{error}</div>}
                </div>
                <div className={styles.modalFooter}>
                    <div className={styles.buttonContainer}>
                        <button className={`${styles.buttonBase} ${styles.saveButton}`} onClick={handleEdit} disabled={!classroomName.trim()}>
                            Save Changes
                        </button>
                        <button className={`${styles.buttonBase} ${styles.cancelButton}`} onClick={triggerClose}>
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditClassroomModal;